import { useAuth } from '@/contexts/auth-context';
import api from '@/libs/axiosInstance';
import {
  BRAND_SURFACE_LOCALES,
  type ApiResponse,
  type BrandSurfaceLocale,
  type SuccessFeeTermsView,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export const SUCCESS_FEE_TERMS_QUERY_KEY = ['success-fee-terms'] as const;

/**
 * The dashboard language, narrowed to a locale the API accepts.
 *
 * The detector can hand back a regional tag (`en-GB`, `fr-CA`) that the
 * acceptance validator, a strict two-value enum, would refuse. Narrowing here
 * means the locale we ask for is the locale we may later echo back.
 */
export function toBrandSurfaceLocale(language: string | undefined): BrandSurfaceLocale {
  const base = (language ?? '').split('-')[0].toLowerCase();
  const supported: readonly string[] = BRAND_SURFACE_LOCALES;

  return supported.includes(base) ? (base as BrandSurfaceLocale) : 'en';
}

const fetchSuccessFeeTerms = async (locale: BrandSurfaceLocale): Promise<SuccessFeeTermsView> => {
  const response = await api.get<ApiResponse<SuccessFeeTermsView>>(
    '/dashboard/legal/success-fee-terms',
    { params: { locale } }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to read the success fee terms');
  }

  return response.data.data;
};

interface UseSuccessFeeTermsOptions {
  enabled?: boolean;
}

/**
 * Where the organization stands against the success-fee terms in force.
 *
 * One query drives all three surfaces: `not_required` shows nothing,
 * `pending_notice` shows the banner, `locked` replaces the dashboard. It stays
 * readable while the dashboard is locked, which is what lets the locked page
 * render the very document that unlocks it.
 *
 * Keyed by locale because the served document, and the hash a proof will point
 * at, differ per language. Invalidating the bare key clears every language at
 * once.
 */
export function useSuccessFeeTermsQuery(options: UseSuccessFeeTermsOptions = {}) {
  const { enabled = true } = options;
  const { i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();

  const locale = toBrandSurfaceLocale(i18n.language);

  return useQuery({
    queryKey: [...SUCCESS_FEE_TERMS_QUERY_KEY, locale],
    queryFn: () => fetchSuccessFeeTerms(locale),
    staleTime: 1000 * 60 * 5,
    retry: 1,
    enabled: enabled && isAuthenticated && !!user?.currentBrandId,
  });
}
